import React, { useState } from "react";
import { Apontamento, Componente } from "./Types";
import blogFetch from "../../../axios/config";

const STATUS_BADGE_CLASSES: Record<string, string> = {
    "NA FILA": "bg-warning text-dark",
    "ERROR": "bg-danger text-white",
    "SUCCESS": "bg-success text-white"
};

const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

interface ModalEditarLoteProps {
    componente: Componente;
    novoLote: string;
    isLoading: boolean;
    onClose: () => void;
    onSave: () => void;
    onLoteChange: (lote: string) => void;
}

const ModalEditarLote: React.FC<ModalEditarLoteProps> = ({
    componente,
    novoLote,
    isLoading,
    onClose,
    onSave,
    onLoteChange
}) => (
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.5)" }}>
        <div className="modal-dialog">
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title">Editar Lote do Componente</h5>
                    <button className="btn-close" onClick={onClose} disabled={isLoading} />
                </div>
                <div className="modal-body">
                    <div className="mb-3">
                        <label className="form-label small text-muted">Componente</label>
                        <p className="fw-semibold mb-2">{componente.apontComp_Componente}</p>
                    </div>
                    <div>
                        <label className="form-label">Novo Lote</label>
                        <input
                            className="form-control"
                            value={novoLote}
                            onChange={(e) => onLoteChange(e.target.value)}
                            placeholder="Digite o novo lote"
                            autoFocus
                        />
                    </div>
                </div>
                <div className="modal-footer">
                    <button
                        className="btn btn-secondary"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        Cancelar
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={onSave}
                        disabled={isLoading || !novoLote.trim()}
                    >
                        {isLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Salvando...
                            </>
                        ) : (
                            "Salvar"
                        )}
                    </button>
                </div>
            </div>
        </div>
    </div>
);

interface TabelaComponentesProps {
    componentes: Componente[];
    isLoading: boolean;
    onEditarLote: (componente: Componente) => void;
}

const TabelaComponentes: React.FC<TabelaComponentesProps> = ({
    componentes,
    isLoading,
    onEditarLote
}) => {
    if (isLoading) {
        return (
            <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status" />
                <p className="text-muted mt-2 mb-0">Carregando componentes...</p>
            </div>
        );
    }

    if (!componentes || componentes.length === 0) {
        return (
            <div className="text-center p-4 text-muted">
                Nenhum componente encontrado.
            </div>
        );
    }

    return (
        <table className="table table-sm table-striped mb-0">
            <thead className="table-secondary">
                <tr>
                    <th>Posição</th>
                    <th>Componente</th>
                    <th>Lote</th>
                    <th className="text-end">Quantidade</th>
                    <th>Status</th>
                    <th style={{ width: 120 }}>Ações</th>
                </tr>
            </thead>
            <tbody>
                {componentes.map((comp) => (
                    <tr key={comp.apontComp_Key}>
                        <td>{comp.apontComp_Posicao}</td>
                        <td className="fw-medium">{comp.apontComp_Componente}</td>
                        <td>
                            <span className="badge bg-light text-dark border">
                                {comp.apontComp_Lote}
                            </span>
                        </td>
                        <td className="text-end">{comp.apontComp_QtdUM}</td>
                        <td>
                            <span className={`badge ${STATUS_BADGE_CLASSES[comp.integration_Status] || "bg-secondary"}`}>
                                {comp.integration_Result || comp.integration_Status}
                            </span>
                        </td>
                        <td>
                            {comp.integration_Status === "ERROR" && (
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => onEditarLote(comp)}
                                >
                                    <i className="bi bi-pencil me-1" />
                                    Editar
                                </button>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default function TabelaApontamentosMontagem({
    apontamentos
}: {
    apontamentos: Apontamento[]
}): JSX.Element {
    const [expanded, setExpanded] = useState<number | null>(null);
    const [componentesCache, setComponentesCache] = useState<Record<number, Componente[]>>({});
    const [isLoadingComponentes, setIsLoadingComponentes] = useState(false);
    const [editandoComponente, setEditandoComponente] = useState<Componente | null>(null);
    const [novoLote, setNovoLote] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const handleToggleExpanded = async (apontKey: number) => {
        if (expanded === apontKey) {
            setExpanded(null);
            return;
        }

        setExpanded(apontKey);

        if (componentesCache[apontKey]) return;

        await buscarComponentes(apontKey);
    };

    const buscarComponentes = async (apontKey: number) => {
        setIsLoadingComponentes(true);
        try {
            const { data } = await blogFetch.get('HistoricoApontamento/GetComponentesApontamento', {
                params: { ApontKey: apontKey }
            });

            setComponentesCache((prev) => ({
                ...prev,
                [apontKey]: data
            }));
        } catch (error) {
            console.error("Erro ao buscar componentes:", error);
            alert("Erro ao carregar componentes. Tente novamente.");
        } finally {
            setIsLoadingComponentes(false);
        }
    };

    const handleEditarLote = (componente: Componente) => {
        setEditandoComponente(componente);
        setNovoLote(componente.apontComp_Lote);
    };

    const handleCancelarEdicao = () => {
        setEditandoComponente(null);
        setNovoLote("");
    };

    const handleSalvarLote = async () => {
        if (!editandoComponente || !novoLote.trim()) return;

        setIsSaving(true);
        try {
            await blogFetch.put("HistoricoApontamento/UpdateComponenteApontamento", {
                ApontComp_Key: editandoComponente.apontComp_Key,
                LoteComponente: novoLote.trim()
            });

            setComponentesCache(prev => {
                const updated = { ...prev };
                Object.keys(updated).forEach(key => {
                    updated[+key] = updated[+key].map(c =>
                        c.apontComp_Key === editandoComponente.apontComp_Key
                            ? { ...c, apontComp_Lote: novoLote.trim() }
                            : c
                    );
                });
                return updated;
            });

            handleCancelarEdicao();
        } catch (error) {
            console.error("Erro ao salvar lote:", error);
            alert("Erro ao alterar lote. Tente novamente.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div className="card shadow-sm">
                <div className="card-body p-0">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th style={{ width: 60 }}></th>
                                <th>Apontamento</th>
                                <th>Centro</th>
                                <th>Ordem</th>
                                <th>Tipo</th>
                                <th>Status</th>
                                <th>Resultado</th>
                                <th>Data/Hora</th>
                                <th>Matrícula</th>
                            </tr>
                        </thead>
                        <tbody>
                            {apontamentos.map((ap) => (
                                <React.Fragment key={ap.apont_Key}>
                                    <tr>
                                        <td>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => handleToggleExpanded(ap.apont_Key)}
                                                disabled={isLoadingComponentes && expanded === ap.apont_Key}
                                                title="Ver componentes"
                                            >
                                                {expanded === ap.apont_Key ? "−" : "+"}
                                            </button>
                                        </td>
                                        <td className="fw-semibold text-primary">{ap.apont_Key}</td>
                                        <td>{ap.centro_Trabalho}</td>
                                        <td>{ap.ordem_Fabricacao}</td>
                                        <td>
                                            <span className="badge bg-light text-dark border">
                                                {ap.tipo_Apontamento}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${STATUS_BADGE_CLASSES[ap.integration_Status] || "bg-secondary"}`}>
                                                {ap.integration_Status}
                                            </span>
                                        </td>
                                        <td>
                                            <small className="text-muted">
                                                {ap.integration_Result.replace(/(ordem\s+)0+(\d+)/i, "$1$2") || "-"}
                                            </small>
                                        </td>
                                        <td>
                                            <small>{formatDateTime(ap._ts_Creat)}</small>
                                        </td>
                                        <td>{ap.nro_Matricula}</td>
                                    </tr>

                                    {expanded === ap.apont_Key && (
                                        <tr className="bg-light">
                                            <td colSpan={9} className="p-4">
                                                <div className="card border">
                                                    <div className="card-header bg-primary text-white fw-semibold">
                                                        Componentes Utilizados
                                                    </div>
                                                    <div className="card-body p-0">
                                                        <TabelaComponentes
                                                            componentes={componentesCache[ap.apont_Key] || []}
                                                            isLoading={isLoadingComponentes}
                                                            onEditarLote={handleEditarLote}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {editandoComponente && (
                <ModalEditarLote
                    componente={editandoComponente}
                    novoLote={novoLote}
                    isLoading={isSaving}
                    onClose={handleCancelarEdicao}
                    onSave={handleSalvarLote}
                    onLoteChange={setNovoLote}
                />
            )}
        </>
    );
}